import { bool, func, object, array, any } from 'prop-types'
import { useState, Fragment, useRef, useLayoutEffect, useMemo } from 'react'
import { Spacing } from '@opallabs/gem'
import {
  Button,
  ButtonIconPosition,
  ButtonSize,
  Popover,
  Icon,
  Link,
  Tooltip,
  ActionsMenu,
} from '@opallabs/lapidary'
import { assetProxyDownloadUrl } from '@opallabs/routing'
import { kuiperClassNames } from '../../utils/kuiperClassNames'
import { AssetOptions } from '../AssetOptions'
import { styles } from './styles'

const overlayMaxWidth = 300
const overlayMinWidth = 150

export function formatDescription(description) {
  if (!description) return ''
  if (description.length <= 25) {
    return description
  }

  const diff = description.length - 25
  // for long descriptions show first and last 10 characters
  return `${description.slice(0, 10)}...${description.slice(diff + 15)}`
}

const ActionButton = ({ label, iconGlyph, handler }) => {
  const icon = useMemo(() => {
    return (
      <Icon
        glyph={iconGlyph}
        ariaHidden={true}
        size={ButtonSize.Small}
        styleOverrides={{
          container: styles.iconContainerOverride,
          icon: styles.iconSvgOverride,
        }}
        useHypernova={!iconGlyph.includes('gem')}
      />
    )
  }, [iconGlyph])

  return (
    <Button
      className={kuiperClassNames(`asset-tool-button asset-tools-${iconGlyph}`)}
      hideButtonText={true}
      icon={icon}
      onClick={handler}
      buttonText={label}
      variant="ghost"
      styleOverrides={{
        button: {
          borderRadius: 16,
        },
      }}
    />
  )
}

function InfoOverlay({
  assetReference,
  coverAsset,
  deletable = false,
  downloadable = true,
  hasAssetOptions,
  isLimitedUser,
  infoOverlayStyles = {},
  onDelete,
  onEmbiggen,
  onRemove,
  options = [],
  removeable = true,
  updateCaption,
  updateCoverAsset,
}) {
  const isImageOrGif =
    assetReference.asset_type === 'image' || assetReference.format === 'gif'
  const isVideo =
    assetReference.asset_type === 'video' && assetReference.format !== 'gif'

  const altTextOption = {
    type: 'textarea',
    content: assetReference.caption,
    assetAltText: assetReference.alt_text,
    option: 'asset alt text',
    required: false,
  }
  const coverImageOption = {
    type: 'file',
    content: coverAsset,
    option: 'cover image',
    required: false,
  }

  const [isHovering, setHovering] = useState(false)
  const [optionsPopoverOpen, setOptionsPopoverOpen] = useState(false)
  const [actionPopoverOpen, setActionPopoverOpen] = useState(false)

  const firstOption = isVideo
    ? coverImageOption
    : isImageOrGif
      ? altTextOption
      : null
  const optionValues = firstOption ? [firstOption, ...options] : options
  // These take into account assets that are too small to fit the filename and/or
  // the asset tools.
  const assetOverlayRef = useRef(null)
  const [showFilenameText, setShowFilenameText] = useState(
    assetReference.width >= overlayMaxWidth
  )
  const [collapseActions, setCollapseActions] = useState(false)

  useLayoutEffect(() => {
    if (!assetOverlayRef.current || assetOverlayRef.current.clientWidth === 0)
      return
    setShowFilenameText(assetOverlayRef.current.clientWidth >= overlayMaxWidth)

    if (
      assetOverlayRef.current?.clientWidth < overlayMinWidth &&
      hasAssetOptions
    ) {
      setCollapseActions(true)
    }
  }, [
    assetOverlayRef.current?.clientWidth,
    setShowFilenameText,
    hasAssetOptions,
    setCollapseActions,
  ])

  return (
    <Fragment>
      <div
        ref={assetOverlayRef}
        css={[styles.assetMeta, infoOverlayStyles]}
        style={{
          opacity:
            isHovering || optionsPopoverOpen || actionPopoverOpen ? 1 : 0,
          zIndex:
            isHovering || optionsPopoverOpen || actionPopoverOpen ? 1000 : 0,
        }}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        className={kuiperClassNames('asset-info-overlay')}
      >
        <div
          className={kuiperClassNames('asset-info-wrapper')}
          css={styles.assetInfoWrapper({ showFilenameText })}
        >
          {typeof onEmbiggen === 'function' || showFilenameText ? (
            <Tooltip content="Expand Asset" zIndex={9999}>
              <Button
                className="kuiper-asset-info-filename"
                css={{ paddingInlineEnd: Spacing.XS }}
                variant="ghost"
                hideButtonText={!showFilenameText}
                onClick={(e) => onEmbiggen?.(e)}
                buttonText={formatDescription(assetReference.filename)}
                icon={
                  typeof onEmbiggen === 'function' ? (
                    <Icon
                      glyph="expand"
                      styleOverrides={{
                        container: styles.iconContainerOverride,
                        icon: styles.iconSvgOverride,
                      }}
                      useHypernova
                    />
                  ) : undefined
                }
                iconPosition={ButtonIconPosition.leading}
              />
            </Tooltip>
          ) : null}

          <div
            className={kuiperClassNames('asset-tools')}
            css={styles.assetTools}
          >
            {hasAssetOptions && (
              <Popover.Root
                onOpenChange={setOptionsPopoverOpen}
                open={optionsPopoverOpen}
              >
                <Popover.Trigger
                  tooltip={{
                    content: 'Asset Options',
                    zIndex: isHovering ? 9999 : 0,
                  }}
                >
                  <Button
                    className={kuiperClassNames(
                      `asset-tool-button asset-tools-cog-lg`
                    )}
                    hideButtonText={true}
                    icon={
                      <Icon
                        glyph="gem-settings"
                        ariaMode="hidden"
                        styleOverrides={{
                          container: styles.iconContainerOverride,
                          icon: styles.iconSvgOverride,
                        }}
                      />
                    }
                    buttonText="Asset Options"
                    variant="ghost"
                    styleOverrides={{
                      button: {
                        borderRadius: 16,
                      },
                    }}
                  />
                </Popover.Trigger>
                <Popover.Content style={{ zIndex: 9999 }}>
                  <AssetOptions
                    isLimitedUser={isLimitedUser}
                    options={optionValues}
                    updateCaption={updateCaption}
                    updateCoverAsset={updateCoverAsset}
                    onSaveAssetOptions={() => {
                      setHovering(false)
                      setOptionsPopoverOpen(false)
                    }}
                    asset={assetReference}
                  />
                </Popover.Content>
              </Popover.Root>
            )}

            {downloadable && !collapseActions && (
              <Tooltip content="Download Asset" zIndex={9999}>
                <div
                  className={kuiperClassNames('asset-tool')}
                  css={styles.assetTool}
                >
                  <Link
                    download={assetReference.filename}
                    href={assetProxyDownloadUrl({ uuid: assetReference.uuid })}
                    target="_blank"
                    rel="noopener noreferrer"
                    styleOverrides={styles.link}
                  >
                    <Icon
                      ariaHidden={true}
                      glyph="download"
                      useHypernova
                      styleOverrides={{
                        container: styles.iconContainerOverride,
                        icon: styles.iconSvgOverride,
                      }}
                    />
                  </Link>
                </div>
              </Tooltip>
            )}

            {deletable && !collapseActions && (
              <Tooltip content="Delete Asset" zIndex={9999}>
                <div
                  className={kuiperClassNames('asset-tool')}
                  css={styles.assetTool}
                >
                  <ActionButton
                    label="Delete"
                    iconGlyph="delete"
                    handler={onDelete}
                  />
                </div>
              </Tooltip>
            )}

            {removeable && !deletable && !collapseActions && (
              <Tooltip content="Remove asset from content" zIndex={9999}>
                <div
                  className={kuiperClassNames('asset-tool')}
                  css={styles.assetTool}
                >
                  <ActionButton
                    label="Remove asset from content"
                    iconGlyph="gem-close"
                    handler={onRemove}
                  />
                </div>
              </Tooltip>
            )}

            {collapseActions && (removeable || deletable || downloadable) && (
              <ActionsMenu.Base
                align="end"
                onOpenChange={setActionPopoverOpen}
                buttonStyles={{ borderRadius: '100%' }}
                contentStyles={{ minInlineSize: 200 }}
                tooltipContent="More Actions"
                zIndex={9999}
                variant="ghost"
              >
                {downloadable && (
                  <ActionsMenu.Item
                    onSelect={() =>
                      window.open(
                        assetProxyDownloadUrl({ uuid: assetReference.uuid })
                      )
                    }
                  >
                    Download Asset
                  </ActionsMenu.Item>
                )}
                {removeable && (
                  <ActionsMenu.Item onSelect={onRemove}>
                    Remove Asset from Content
                  </ActionsMenu.Item>
                )}
                {deletable && (
                  <ActionsMenu.Item onSelect={onDelete}>
                    Delete Asset
                  </ActionsMenu.Item>
                )}
              </ActionsMenu.Base>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  )
}

InfoOverlay.propTypes = {
  assetReference: object,
  coverAsset: object,
  deletable: bool,
  downloadable: bool,
  hasAssetOptions: bool,
  infoOverlayStyles: any,
  isLimitedUser: bool,
  onDelete: func,
  onEmbiggen: func,
  onRemove: func,
  options: array,
  removeable: bool,
  updateCaption: func,
  updateCoverAsset: func,
}

export default InfoOverlay
